import * as React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Clock, ArrowRight, ChevronDown, Shield } from 'lucide-react';
import {
  DOCTOR_PLANS,
  STUDIO_PLANS,
  CLINIC_PLANS,
  TRIAL_DURATION_DAYS,
  TRIAL_REPORT_CAP,
} from '@/lib/plans';
import { cn } from '@/lib/utils';
import { SalesContactForm } from '@/components/pricing/SalesContactForm';

/**
 * Card prezzi unificate — Pivot Pricing v3.0 (2026-07-19)
 * UN SOLO template (`PlanCardV3`) per Individuale (Trial + Solo) e Struttura.
 * Solo i DATI cambiano; struttura HTML/CSS, spaziatura, badge sono identici.
 * Fonte dati: src/lib/plans.ts. I piani legacy sono nascosti automaticamente.
 */

interface CardFeature {
  text: string;
  /** true = check muted (trial/preview), false/undefined = check primary */
  muted?: boolean;
  /** enfasi forense: bordo sinistro + icona Shield */
  highlight?: boolean;
}

interface PlanCardV3Data {
  id: string;
  name: string;
  /** Prezzo €. Ignorato se `customPriceLabel` è valorizzato (Enterprise). */
  price?: number;
  period: string;
  /** Etichetta prezzo custom (es. "Su misura") per piani senza prezzo fisso. */
  customPriceLabel?: string;
  reportsLabel: string;
  /** mostra il badge "Tutto incluso" in alto sinistra + bordo primario */
  showAllIncludedBadge?: boolean;
  features: CardFeature[];
  ctaLabel: string;
  ctaVariant?: 'default' | 'outline';
  /** Override completo della CTA (es. SalesContactForm per Enterprise). */
  renderCta?: (defaultButton: React.ReactNode) => React.ReactNode;
}

interface PlanCardV3Props {
  card: PlanCardV3Data;
  compact?: boolean;
  onCta?: () => void;
  allIncludedBadgeLabel?: string;
}

/**
 * Template unico per tutte le card piano (Trial / Individuale / Struttura / Enterprise).
 * NON aggiungere varianti di layout: qualsiasi differenza va gestita via dati.
 * - Badge "Tutto incluso": SOLO su piani con prezzo fisso full-featured.
 *   MAI su Enterprise (prezzo "su misura" → il badge non ha significato).
 * - Prezzo custom: quando `customPriceLabel` è valorizzato, sostituisce €/mese.
 * - CTA custom: `renderCta` avvolge il Button di default (es. SalesContactForm).
 */
function PlanCardV3({
  card,
  compact = false,
  onCta,
  allIncludedBadgeLabel = 'Tutto incluso',
}: PlanCardV3Props) {
  const showBadge = !!card.showAllIncludedBadge;
  const defaultButton = (
    <Button
      size="sm"
      variant={card.ctaVariant ?? 'default'}
      className="w-full mt-auto rounded-md"
      onClick={onCta}
    >
      {card.ctaLabel}
      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
    </Button>
  );
  return (
    <Card
      className={cn(
        'relative flex flex-col rounded-lg transition-colors bg-card',
        showBadge ? 'border-2 border-primary' : 'border border-border',
        compact ? 'p-6' : 'p-7 sm:p-9',
        // spazio extra in alto quando la cornice del badge sporge sopra il card
        showBadge && 'pt-8 sm:pt-10',
      )}
    >
      {showBadge && (
        <div className="absolute -top-2.5 left-6 px-2 py-0.5 rounded-xs text-[10px] font-semibold tracking-[0.14em] uppercase bg-background text-primary ring-1 ring-primary/30 whitespace-nowrap z-10 no-underline [text-decoration:none]">
          {allIncludedBadgeLabel}
        </div>
      )}

      <h3
        className="font-semibold text-xl sm:text-2xl text-foreground mb-4 leading-tight break-words"
        style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
      >
        {card.name}
      </h3>

      <div className="flex items-baseline gap-1.5 mb-1.5 flex-wrap">
        {card.customPriceLabel ? (
          <span className="text-[28px] sm:text-[32px] font-semibold text-foreground leading-none">
            {card.customPriceLabel}
          </span>
        ) : (
          <>
            <span className="text-[32px] font-semibold text-foreground tabular-nums leading-none">
              €{card.price ?? 0}
            </span>
            <span className="text-xs text-muted-foreground">{card.period}</span>
          </>
        )}
      </div>

      <div className="mt-3 mb-5 inline-flex items-center gap-1.5 self-start text-xs font-medium text-foreground-soft">
        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
        {card.reportsLabel}
      </div>

      <div className="mb-4 h-px bg-border/60" />

      <ul className="space-y-2.5 mb-6 flex-1">
        {card.features.map((f, i) => (
          <li
            key={i}
            className={cn(
              'flex gap-2 text-xs leading-snug break-words',
              f.highlight
                ? 'rounded-xs border-l-2 border-primary bg-primary/[0.03] px-2.5 py-2 text-foreground'
                : f.muted
                  ? 'text-muted-foreground'
                  : 'text-foreground',
            )}
          >
            {f.highlight ? (
              <Shield className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
            ) : (
              <Check
                className={cn(
                  'h-3.5 w-3.5 shrink-0 mt-0.5',
                  f.muted ? 'text-muted-foreground/70' : 'text-primary',
                )}
              />
            )}
            <span className="min-w-0">{f.text}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        {card.renderCta ? card.renderCta(defaultButton) : defaultButton}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Individuale (Trial + Solo)
// ---------------------------------------------------------------------------

const solo = DOCTOR_PLANS.find(p => p.id === 'solo')!;

const DOCTOR_CARDS: PlanCardV3Data[] = [
  {
    id: 'trial',
    name: `Prova gratuita ${TRIAL_DURATION_DAYS} giorni`,
    price: 0,
    period: 'per 14 giorni',
    reportsLabel: `Fino a ${TRIAL_REPORT_CAP} crediti totali`,
    ctaVariant: 'outline',
    ctaLabel: 'Inizia gratis',
    features: [
      { text: 'Accesso completo a tutte le feature', muted: true },
      { text: 'Verifica medico-legale pre-firma', muted: true },
      { text: 'Nessuna carta di credito richiesta', muted: true },
    ],
  },
  {
    id: 'solo',
    name: solo.name,
    price: solo.price ?? 0,
    period: solo.period,
    reportsLabel: `${solo.reports} crediti/mese di capacità`,
    showAllIncludedBadge: true,
    ctaVariant: 'default',
    ctaLabel: `Scegli ${solo.name}`,
    features: [
      { text: `${solo.reports} crediti/mese di capacità inclusa` },
      { text: 'Accesso completo a tutte le feature cliniche' },
      { text: 'Verifica medico-legale pre-firma' },
      { text: 'Archivio referti illimitato' },
      { text: 'Carta intestata personalizzata' },
      {
        text: 'Integrità documentale verificabile: impronta SHA-256 e marca temporale RFC 3161 con QR di verifica; dopo la finalizzazione il referto non è più modificabile',
        highlight: true,
      },
      { text: 'Capacità aggiuntiva acquistabile quando serve (+50, +100, +250, +500 crediti)' },
      { text: 'Generazione prioritaria + Supporto prioritario' },
    ],
  },
];

interface IncrementalDoctorPlansProps {
  onSelect: (planId: string) => void;
  ctaLabel?: (planId: string) => string;
  compact?: boolean;
  hideTrial?: boolean;
}

export function IncrementalDoctorPlans({
  onSelect,
  ctaLabel,
  compact = false,
  hideTrial = false,
}: IncrementalDoctorPlansProps) {
  return (
    <div className="grid gap-6 grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto items-stretch">
      {DOCTOR_CARDS.filter((card) => !hideTrial || card.id !== 'trial').map((card) => (
        <PlanCardV3
          key={card.id}
          card={ctaLabel ? { ...card, ctaLabel: ctaLabel(card.id) } : card}
          compact={compact}
          onCta={() => onSelect(card.id)}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Struttura (studio: `structure`) — STESSO template di Individuale
// ---------------------------------------------------------------------------

interface StructurePlansV3Props {
  onSelect: (planId: string) => void;
  ctaLabel?: (planId: string) => string;
  compact?: boolean;
}

export function StructurePlansV3({
  onSelect,
  ctaLabel,
  compact = false,
}: StructurePlansV3Props) {
  const cards: PlanCardV3Data[] = React.useMemo(() => {
    const structurePlans = STUDIO_PLANS.filter(p => !p.legacy);
    return structurePlans.map((plan) => {
      const included = plan.features.filter(f => f.included);
      const features: CardFeature[] = included.map(f => {
        const isForensic = /SHA-?256|RFC\s*3161|medico-?legale|forense/i.test(f.text);
        return { text: f.text, highlight: isForensic };
      });
      return {
        id: plan.id,
        name: plan.name,
        price: plan.price ?? 0,
        period: plan.period,
        reportsLabel: `${plan.reports} crediti/mese condivisi`,
        showAllIncludedBadge: true,
        ctaVariant: 'default' as const,
        ctaLabel: `Attiva ${plan.name}`,
        features,
      };
    });
  }, []);

  const isSingle = cards.length === 1;
  return (
    <div
      className={cn(
        'grid gap-6 grid-cols-1 items-stretch mx-auto',
        isSingle ? 'max-w-md' : 'md:grid-cols-2 max-w-4xl',
      )}
    >
      {cards.map((card) => (
        <PlanCardV3
          key={card.id}
          card={ctaLabel ? { ...card, ctaLabel: ctaLabel(card.id) } : card}
          compact={compact}
          onCta={() => onSelect(card.id)}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Enterprise (CLINIC_PLANS: id='enterprise') — STESSO template, senza badge
// ---------------------------------------------------------------------------

interface EnterpriseCardV3Props {
  compact?: boolean;
  /** Label CTA custom, default "Contatta vendite". */
  ctaLabel?: string;
}

/**
 * Card Enterprise unificata al template PlanCardV3.
 * IMPORTANTE: NIENTE badge "Tutto incluso" — il piano è a prezzo custom
 * ("Su misura"), quindi il badge non ha significato semantico.
 */
export function EnterpriseCardV3({ compact = false, ctaLabel = 'Contatta vendite' }: EnterpriseCardV3Props) {
  const enterprise = CLINIC_PLANS.find(p => p.id === 'enterprise');
  if (!enterprise) return null;

  const included = enterprise.features.filter(f => f.included);
  const features: CardFeature[] = included.map(f => {
    const isForensic = /SHA-?256|RFC\s*3161|medico-?legale|forense/i.test(f.text);
    return { text: f.text, highlight: isForensic };
  });

  const card: PlanCardV3Data = {
    id: enterprise.id,
    name: enterprise.name,
    customPriceLabel: 'Su misura',
    period: '',
    reportsLabel: 'Medici e volumi illimitati',
    showAllIncludedBadge: false,
    ctaVariant: 'outline',
    ctaLabel,
    features,
    renderCta: () => (
      <SalesContactForm
        trigger={
          <Button size="sm" variant="outline" className="w-full rounded-md">
            {ctaLabel}
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        }
      />
    ),
  };

  return (
    <div className="grid gap-6 grid-cols-1 max-w-md mx-auto items-stretch">
      <PlanCardV3 card={card} compact={compact} />
    </div>
  );
}

/**
 * Accordion "Confronta tutte le funzioni piano per piano"
 * Filtra sempre i piani legacy (Pivot Pricing v3.0).
 */
interface FullFeatureComparisonProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  id?: string;
  audience?: 'doctor' | 'studio' | 'clinic' | 'structure' | 'all';
}

export function FullFeatureComparison({
  open,
  onOpenChange,
  id,
  audience = 'all',
}: FullFeatureComparisonProps) {
  const [uncontrolled, setUncontrolled] = React.useState(false);
  const isOpen = open ?? uncontrolled;
  const toggle = () => {
    if (onOpenChange) onOpenChange(!isOpen);
    else setUncontrolled(v => !v);
  };

  const allGroups = [
    { key: 'doctor' as const, label: 'Medici singoli', plans: DOCTOR_PLANS.filter(p => p.id !== 'test' && !p.legacy) },
    { key: 'studio' as const, label: 'Studi', plans: STUDIO_PLANS.filter(p => !p.legacy) },
    { key: 'clinic' as const, label: 'Strutture', plans: CLINIC_PLANS.filter(p => !p.legacy) },
  ];

  const visibleGroups = allGroups.filter(g => {
    if (audience === 'all') return true;
    if (audience === 'structure') return g.key === 'studio' || g.key === 'clinic';
    return g.key === audience;
  });

  return (
    <div id={id} className="max-w-3xl mx-auto">
      <div
        className="rounded-xl border border-border bg-card overflow-hidden cursor-pointer transition-colors hover:border-primary/30"
        onClick={toggle}
      >
        <div className="flex items-center justify-between p-4">
          <span className="text-sm font-medium text-foreground-soft">
            Confronta tutte le funzioni piano per piano
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              isOpen && 'rotate-180',
            )}
          />
        </div>
        {isOpen && (
          <div className="border-t border-border p-4 sm:p-5 space-y-6">
            {visibleGroups.map(group => (
              <div key={group.label}>
                <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-3">
                  {group.label}
                </p>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {group.plans.map(plan => (
                    <div key={plan.id} className="rounded-lg border border-border/60 p-3">
                      <p className="text-sm font-semibold text-foreground mb-2">
                        {plan.name}
                      </p>
                      <ul className="space-y-1">
                        {plan.features.map((f, i) => (
                          <li
                            key={i}
                            className="flex items-start justify-between gap-2 text-[11px] leading-snug"
                          >
                            <span className="text-muted-foreground">{f.text}</span>
                            <span
                              className={cn(
                                'font-medium shrink-0',
                                f.included ? 'text-primary' : 'text-muted-foreground/40',
                              )}
                            >
                              {f.included ? 'Sì' : 'No'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
